import { PlatformBrand } from '@/components/passport/PlatformBrand';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, apiFetch } from '@/lib/api';
import { usePassportStore } from '@/stores/passport-store';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { PassportEventSummaryListDto, PassportLoginOutput } from '@tunodo/ticketing-shared';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/passport/')({
  beforeLoad: () => {
    const { user } = usePassportStore.getState();
    if (user) {
      throw redirect({ to: '/passport/events' });
    }
  },
  component: PassportLoginPage,
});

function PassportLoginPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);
  const setUser = usePassportStore((s) => s.setUser);
  const pendingStampToken = usePassportStore((s) => s.pendingStampToken);
  const navigate = useNavigate();

  const emailSchema = z.string().email();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEmail(e.target.value);
    setError(null);
    setNetworkError(null);
    setFormatError(null);
  }

  function handleBlur() {
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setFormatError('Ingresa un correo válido, por ej. nombre@dominio.com');
    } else {
      setFormatError(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (formatError) return;
    setError(null);
    setNetworkError(null);
    setIsLoading(true);
    try {
      const result = await apiFetch('/api/passport/login', {
        method: 'POST',
        body: JSON.stringify({ email }),
        schema: PassportLoginOutput,
      });
      setUser({ userId: result.userId, email: result.email });
      if (pendingStampToken) {
        await navigate({ to: '/passport/stamp', search: { token: pendingStampToken } });
      } else {
        const events = await apiFetch(`/api/passport/${result.userId}/events`, {
          schema: PassportEventSummaryListDto,
        });
        const onlyEvent = events.length === 1 ? events[0] : undefined;
        if (onlyEvent) {
          await navigate({ to: '/passport/events/$eventId', params: { eventId: onlyEvent.id } });
        } else {
          await navigate({ to: '/passport/events' });
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 400 || err.status === 404) {
          setError(
            'No encontramos un pasaporte para ese correo. Verifica que sea el mismo que usaste al inscribirte.',
          );
        } else {
          setError('Ocurrió un error inesperado. Intenta de nuevo.');
        }
      } else {
        setNetworkError('No pudimos conectar con el servidor. Intenta de nuevo en un momento.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-4">
      <PlatformBrand className="absolute top-4 left-1/2 -translate-x-1/2" />
      {networkError && (
        <Alert variant="destructive" className="w-full max-w-sm">
          <AlertDescription>{networkError}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col items-center gap-3">
        <img src="/cimoips-color.png" alt="CIMO IPS" className="h-16" />
        <h1 className="text-2xl font-bold tracking-tight">Pasaporte CIMO IPS 2026</h1>
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          Ingresa tu email para acceder a tu pasaporte de ponencias
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex w-full max-w-sm flex-col gap-3">
        <Input
          type="email"
          placeholder="tu@email.com"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          Usa el correo con el que te inscribiste al evento.
        </p>
        {formatError && !error && <p className="text-sm text-red-600">{formatError}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button
          type="submit"
          disabled={isLoading || !email || !!formatError}
          style={{ backgroundColor: '#522B9A' }}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Buscando…
            </>
          ) : (
            'Buscar mi pasaporte'
          )}
        </Button>
      </form>

      <div className="flex w-full max-w-sm flex-col items-center gap-1 rounded-xl border border-dashed border-muted-foreground/30 bg-muted/40 px-4 py-3 text-center">
        <p className="text-xs text-muted-foreground">
          ¿No te registraste o pusiste un correo diferente?
        </p>
        <a
          href="https://forms.gle/miHkhVe4yRTbfwA59"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium underline underline-offset-2"
          style={{ color: '#522B9A' }}
        >
          Diligencia este formulario para crear tu pasaporte virtual
        </a>
      </div>
    </main>
  );
}
